import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { axiosInstance } from '../../helpers'
import type { PipelineDefinition } from '../LeasingSeason/Configurations/PipelineBuilder/pipelineBuilder.types'
import {
    ORG_LEADS_PIPELINE_CONFIGS_KEY,
    ORG_PIPELINE_CONFIGS_KEY,
    PROPERTY_PIPELINE_CONFIGS_KEY,
    orgLeadsPipelineConfigsKey,
    orgLeadsPipelineConfigKey,
} from '../LeasingSeason/Configurations/PipelineBuilder/usePipelineConfigApi'

export interface OrgLeadsPipelineConfigListItem {
    id: number
    name: string
    created_from_template_key?: string | null
    created_from_template_version?: number | null
    is_editable: boolean
    created_date?: string
    modified_date?: string
}

export interface OrgLeadsPipelineConfigDetail {
    id: number
    name: string
    definition: PipelineDefinition
    created_from_template_key?: string | null
    created_from_template_version?: number | null
    is_editable: boolean
    created_date?: string
    modified_date?: string
}

export interface OrgLeadsPipelineApplyResult {
    created: number
    updated: number
    skipped_locked: number
    skipped_missing: number
}

export function extractOrgPipelineNameError(error: unknown): string | null {
    const data = (error as { response?: { data?: { name?: string[] } } })
        ?.response?.data
    const nameErrors = data?.name
    if (Array.isArray(nameErrors) && nameErrors[0]) {
        return nameErrors[0]
    }
    return null
}

export async function fetchOrgLeadsPipelineDefinition(
    organizationId: number,
    configId: number,
): Promise<PipelineDefinition | null> {
    const { data } = await axiosInstance.get<OrgLeadsPipelineConfigDetail>(
        `/company/organization/${organizationId}/pipeline-configs/${configId}/`,
    )
    return data.definition
}

export function useOrgLeadsPipelineConfigs(
    organizationId: number | null | undefined,
) {
    return useQuery({
        queryKey: orgLeadsPipelineConfigsKey(organizationId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                OrgLeadsPipelineConfigListItem[]
            >(`/company/organization/${organizationId}/pipeline-configs/`)
            return data
        },
        enabled: organizationId != null,
        refetchOnWindowFocus: false,
    })
}

export function useOrgLeadsPipelineConfig(
    organizationId: number | null | undefined,
    configId: number | null | undefined,
) {
    return useQuery({
        queryKey: orgLeadsPipelineConfigKey(organizationId, configId),
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<OrgLeadsPipelineConfigDetail>(
                `/company/organization/${organizationId}/pipeline-configs/${configId}/`,
            )
            return data
        },
        enabled: organizationId != null && configId != null,
        refetchOnWindowFocus: false,
    })
}

export function useCreateOrgLeadsPipelineConfig(
    organizationId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            name: string
            template_key?: string
            definition?: PipelineDefinition
        }) => {
            const body: {
                name: string
                template_key?: string
                definition?: PipelineDefinition
            } = { name: payload.name }

            if (payload.template_key) {
                body.template_key = payload.template_key
            } else if (payload.definition) {
                body.definition = payload.definition
            }

            const {
                data,
            } = await axiosInstance.post<OrgLeadsPipelineConfigDetail>(
                `/company/organization/${organizationId}/pipeline-configs/`,
                body,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: orgLeadsPipelineConfigsKey(organizationId),
            })
        },
    })
}

export function useSaveOrgLeadsPipelineConfig(
    organizationId: number | null | undefined,
    configId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            name?: string
            definition?: PipelineDefinition
        }) => {
            const {
                data,
            } = await axiosInstance.patch<OrgLeadsPipelineConfigDetail>(
                `/company/organization/${organizationId}/pipeline-configs/${configId}/`,
                payload,
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.setQueryData(
                orgLeadsPipelineConfigKey(organizationId, configId),
                data,
            )
            queryClient.invalidateQueries({
                queryKey: orgLeadsPipelineConfigsKey(organizationId),
            })
        },
    })
}

export function useApplyOrgLeadsPipelineToProperties(
    organizationId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            configId: number
            property_ids: number[]
            name?: string
            signable_doc_overrides?: Record<string, number>
            auto_provision_signable_docs?: boolean
        }) => {
            const {
                data,
            } = await axiosInstance.post<OrgLeadsPipelineApplyResult>(
                `/company/organization/${organizationId}/pipeline-configs/${payload.configId}/apply/`,
                {
                    property_ids: payload.property_ids,
                    name: payload.name,
                    signable_doc_overrides: payload.signable_doc_overrides,
                    auto_provision_signable_docs:
                        payload.auto_provision_signable_docs,
                },
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PROPERTY_PIPELINE_CONFIGS_KEY],
            })
            queryClient.invalidateQueries({
                queryKey: [ORG_PIPELINE_CONFIGS_KEY],
            })
            queryClient.invalidateQueries({
                queryKey: [ORG_LEADS_PIPELINE_CONFIGS_KEY],
            })
        },
    })
}
